import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { distinctUntilChanged, debounceTime, switchMap } from 'rxjs/operators';
import { IManagerProfile } from 'src/app/core/models/i-manager-profile';
import { ISearchResponse } from 'src/app/core/models/i-search-response';
import { IAutoCompleteData } from 'src/app/shared-form-sections/interfaces/i-auto-complete-data';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { ExternalDataService } from 'src/app/shared/services/external-data.service';
import { ManagerService } from 'src/app/shared/services/manager.service';

class ManagerDashboardComponentBase {
  constructor() {}
}

const ManagerDashboardComponentMixinBase = WithProcessing(
  ManagerDashboardComponentBase
);

@Component({
  selector: 'app-manager-dashboard',
  templateUrl: './manager-dashboard.component.html',
  styleUrls: ['./manager-dashboard.component.scss'],
})
export class ManagerDashboardComponent
  extends ManagerDashboardComponentMixinBase
  implements OnInit, OnDestroy
{
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  subscriptions: Subscription[] = [];
  dataSource = new MatTableDataSource<IManagerProfile>();

  // Pagination Variables
  totalCount = 0;
  pageNumbers = [1, 2, 3, 4, 5];
  maxNumberOfPageButtons = 5;
  pageTracker = 1;
  pagingOptions: string[] = ['10'];
  pageSize = parseInt(this.pagingOptions[0], 10);
  defaultPageSize = 20;
  DISPLAYED_COLUMNS: string[] = ['name', 'languages', 'lastUpdatedOn'];

  pageSizeFilter = new UntypedFormControl();
  currentPageNumber: number;

  // Manager
  isSearchingManager: boolean = false;
  managerSearchFormControl: FormControl = new FormControl('');
  managerSearchResults: IAutoCompleteData[];
  placeholder: string = 'Managers';

  constructor(
    public externalDataService: ExternalDataService,
    private managerService: ManagerService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.selectPage(1);

    const pageSizeSubscription = this.pageSizeFilter.valueChanges.subscribe(
      (x) => this.selectPageSize(x)
    );

    this.initManagerSearch();

    this.subscriptions.push(pageSizeSubscription);
  }

  initManagerSearch() {
    this.managerSearchFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        switchMap((searchedValue: string) => {
          const getManagersObs$ = searchedValue
            ? this.externalDataService.searchManagers$(searchedValue)
            : of(null);

          return getManagersObs$.pipe();
        })
      )
      .subscribe({
        next: (searchResults: ISearchResponse[]) => {
          if (searchResults) {
            this.managerSearchResults = searchResults.map((p) => {
              return { displayName: p.title, id: p.id.toString() };
            });
          }
        },
      });
  }

  onSelectManager($event) {
    this.router.navigate([
      this.router.url.split('?')[0],
      $event.option.value.id,
    ]);
  }

  loadManagerData(): void {
    const managerGetSubscription = this.process$(
      this.managerService.getAllManagerProfiles$(
        this.pageTracker,
        this.pageSize
      )
    ).subscribe((data) => {
      this.dataSource.paginator = this.paginator;
      this.dataSource.filterPredicate = (): boolean => true;
      this.dataSource.data = data.managerProfiles;
      this.totalCount = data.totalCount;
      this.dataSource.sort = this.sort;

      this.populatePageButtonArray();

      this.pageSizeFilter.setValue(this.pageSize.toString(), {
        emitEvent: false,
      });
    });

    this.subscriptions.push(managerGetSubscription);
  }

  getLanguages(bio) {
    return Object.keys(bio).join(', ');
  }

  populatePageButtonArray(): void {
    const numberOfPagesToShow = 5;

    if (this.totalCount === 0) {
      this.pageNumbers = [];
      return;
    }
    const maxPage = Math.ceil(this.totalCount / this.pageSize);

    const currentPageIndex = this.pageTracker;

    const start = currentPageIndex - 3 <= 0 ? 1 : currentPageIndex - 2;
    let end = start + (numberOfPagesToShow - 1);
    if (start + (numberOfPagesToShow - 1) > maxPage) {
      end = maxPage;
    }

    this.pageNumbers = [];
    for (let i = start; i <= end; i++) {
      this.pageNumbers.push(i);
    }
  }

  isActivePage(currentIndex): boolean {
    return this.pageTracker === currentIndex;
  }

  // pagination handlers
  hasPreviousPage(): boolean {
    return this.pageTracker > 1;
  }

  hasNextPage(): boolean {
    return this.pageTracker < Math.ceil(this.totalCount / this.pageSize);
  }

  loadNextPage(): void {
    this.pageTracker = this.pageTracker + 1;
    this.setPageParam();
    this.loadManagerData();
  }

  loadPreviousPage(): void {
    this.pageTracker = this.pageTracker - 1;
    this.setPageParam();
    this.loadManagerData();
  }

  selectPage(index: number): void {
    this.pageTracker = index;
    this.setPageParam();
    this.loadManagerData();
  }

  selectPageSize(selectedPageSize: string): void {
    this.pageSize = parseInt(selectedPageSize);
    this.selectPage(1);
  }

  setPageParam() {
    if (this.pageTracker > 1) {
      const queryParams: Params = { page: this.pageTracker };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    } else if ((this.pageTracker = 1)) {
      const queryParams: Params = { page: null };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
